import {
  Subscriptions_PaymentProcessor as PaymentProcessor,
  Subscriptions_ProductType as ProductType,
  Subscriptions_Status as Status,
} from '__generated__/graphql-types';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import * as PaymentProcessorTypes from 'bundles/payments-common/common/PaymentProcessorTypes';
import * as ProductTypes from 'bundles/payments-common/common/ProductType';
import type { SubscriptionFieldsFragment as Subscription } from 'bundles/subscriptions-common/queries/__generated__/QueryMySubscriptions';

/** Consumers do arithmetic on these, so ISO DateTime strings are converted at construction. */
export const dateTimeToEpochMs = (dateTime?: string | null): number | undefined => {
  if (!dateTime) {
    return undefined;
  }
  const epochMs = Date.parse(dateTime);
  return Number.isNaN(epochMs) ? undefined : epochMs;
};

const toLegacyCartId = (cartId?: string | null): number | undefined => {
  if (!cartId) {
    return undefined;
  }

  const legacyCartId = Number(cartId);
  return Number.isNaN(legacyCartId) ? undefined : legacyCartId;
};

const STATUSES_WITH_NEXT_BILL_DETAILS: Array<Subscription['status']> = [
  Status.FreeTrial,
  Status.Active,
  Status.PastDue,
];

const APPLE_IN_APP_PROCESSORS: Array<Subscription['paymentProcessor']> = [
  PaymentProcessor.AppleInapp,
  PaymentProcessor.AppleInappSandbox,
];

/** Non-Stripe Wallet-funded subscriptions processors */
const WALLET_PROCESSORS: Array<Subscription['paymentProcessor']> = [
  PaymentProcessor.Braintree,
  PaymentProcessor.BraintreeSandbox,
];

/** Every Stripe entity, matching naptime's entity-agnostic Stripe union member. */
const STRIPE_PROCESSORS: Array<Subscription['paymentProcessor']> = [
  PaymentProcessor.StripeConsumer,
  PaymentProcessor.StripeConsumerSandbox,
  PaymentProcessor.StripeBvConsumer,
  PaymentProcessor.StripeBvConsumerSandbox,
];

const STRIPE_BV_PROCESSORS: Array<Subscription['paymentProcessor']> = [
  PaymentProcessor.StripeBvConsumer,
  PaymentProcessor.StripeBvConsumerSandbox,
];

const RAZORPAY_PROCESSORS: Array<Subscription['paymentProcessor']> = [
  PaymentProcessor.Razorpay,
  PaymentProcessor.RazorpaySandbox,
];

/**
 * Scoped to Stripe and Razorpay: those are the only members naptime carries an `isSandbox` flag on,
 * so widening this would diverge from the shim mid-migration. Add other processors post-cutover.
 */
const SANDBOX_PROCESSORS: Array<Subscription['paymentProcessor']> = [
  PaymentProcessor.StripeConsumerSandbox,
  PaymentProcessor.StripeBvConsumerSandbox,
  PaymentProcessor.RazorpaySandbox,
];

const PAYMENT_PROCESSOR_IDS: Record<Subscription['paymentProcessor'], string> = {
  [PaymentProcessor.AppleInapp]: PaymentProcessorTypes.appleInapp,
  [PaymentProcessor.AppleInappSandbox]: PaymentProcessorTypes.appleInappSandbox,
  [PaymentProcessor.Braintree]: PaymentProcessorTypes.braintree,
  [PaymentProcessor.BraintreeSandbox]: PaymentProcessorTypes.braintreeSandbox,
  [PaymentProcessor.CourseraManual]: PaymentProcessorTypes.coursera,
  [PaymentProcessor.Dlocal]: PaymentProcessorTypes.dlocal,
  [PaymentProcessor.DlocalSandbox]: PaymentProcessorTypes.dlocalSandbox,
  [PaymentProcessor.Ebanx]: 'ebanx',
  [PaymentProcessor.EbanxSandbox]: 'ebanxSandbox',
  [PaymentProcessor.Emergent]: 'emergent',
  [PaymentProcessor.EmergentSandbox]: 'emergentSandbox',
  [PaymentProcessor.FinancialAid]: PaymentProcessorTypes.financialAid,
  [PaymentProcessor.GooglePlay]: 'googlePlay',
  [PaymentProcessor.GooglePlaySandbox]: 'googlePlaySandbox',
  [PaymentProcessor.Mock]: PaymentProcessorTypes.mockPaymentProcessor,
  [PaymentProcessor.Paypal]: PaymentProcessorTypes.paypal,
  [PaymentProcessor.PaypalSandbox]: PaymentProcessorTypes.paypalSandbox,
  [PaymentProcessor.Razorpay]: PaymentProcessorTypes.razorpay,
  [PaymentProcessor.RazorpayChargeAtWill]: PaymentProcessorTypes.razorpayChargeAtWill,
  [PaymentProcessor.RazorpayChargeAtWillSandbox]: PaymentProcessorTypes.razorpayChargeAtWillSandbox,
  [PaymentProcessor.RazorpaySandbox]: PaymentProcessorTypes.razorpaySandbox,
  [PaymentProcessor.Stripe]: PaymentProcessorTypes.stripe,
  [PaymentProcessor.StripeBvConsumer]: PaymentProcessorTypes.stripeBvConsumer,
  [PaymentProcessor.StripeBvConsumerSandbox]: PaymentProcessorTypes.stripeBvConsumerSandbox,
  [PaymentProcessor.StripeConsumer]: PaymentProcessorTypes.stripeConsumer,
  [PaymentProcessor.StripeConsumerSandbox]: PaymentProcessorTypes.stripeConsumerSandbox,
  [PaymentProcessor.StripeSandbox]: PaymentProcessorTypes.stripeSandbox,
  [PaymentProcessor.StripeThirdParty]: 'stripeThirdParty',
  [PaymentProcessor.StripeThirdPartySandbox]: 'stripeThirdPartySandbox',
  [PaymentProcessor.Zerodollar]: PaymentProcessorTypes.zeroDollar,
};

const toPaymentProcessorId = (paymentProcessor: Subscription['paymentProcessor']): string =>
  PAYMENT_PROCESSOR_IDS[paymentProcessor];

const PRODUCT_TYPES: Record<Subscription['productId']['productType'], ProductTypes.ProductType | undefined> = {
  [ProductType.CatalogSubscription]: ProductTypes.CATALOG_SUBSCRIPTION,
  [ProductType.CourseraPlusSubscription]: ProductTypes.COURSERA_PLUS_SUBSCRIPTION,
  [ProductType.CourseraTierLite]: ProductTypes.COURSERA_TIER_LITE,
  [ProductType.CredentialTrackSubscription]: ProductTypes.CREDENTIAL_TRACK_SUBSCRIPTION,
  [ProductType.CredentialTrackSubscriptionV2]: ProductTypes.CREDENTIAL_TRACK_SUBSCRIPTION_V2,
  [ProductType.Invalid]: undefined,
  [ProductType.Specialization]: ProductTypes.SPECIALIZATION,
  [ProductType.SpecializationSubscription]: ProductTypes.SPECIALIZATION_SUBSCRIPTION,
};

const toProductType = (productType: Subscription['productId']['productType']): ProductTypes.ProductType | undefined =>
  PRODUCT_TYPES[productType];

const toLegacyProductSku = ({ productItemId, productType }: Subscription['productSku']): string | undefined => {
  const legacyProductType = toProductType(productType);
  return legacyProductType ? tupleToStringKey([legacyProductType, productItemId]) : undefined;
};

/**
 * Model over the gateway `Subscriptions_Subscription`, replacing the naptime `subscriptions.v1`
 * resource on the read path. Constructed at the fetch boundary; exposes the same getters consumers
 * call today.
 */
class Subscriptions {
  declare id: string;

  declare cartId?: number;

  declare isActive: boolean;

  /** Epoch ms */
  declare startsAt?: number;

  /** Epoch ms */
  declare nextBillingAt?: number;

  declare status: Subscription['status'];

  declare paymentProcessor: Subscription['paymentProcessor'];

  declare paymentProcessorSubscriptionId: string;

  /**
   * The payment method funding this Subscription. Populated for Stripe Subscriptions too (with the
   * Stripe paymentMethodId), not only wallet-based ones — confirmed with BE.
   */
  declare paymentWalletId?: string;

  declare productId: Subscription['productId'];

  declare productSku?: string;

  constructor({
    id,
    cartId,
    isActive,
    startsAt,
    nextBillingAt,
    status,
    paymentProcessor,
    paymentProcessorSubscriptionId,
    paymentWalletId,
    productId,
    productSku,
  }: Subscription) {
    this.id = id;
    this.cartId = toLegacyCartId(cartId);
    this.isActive = isActive;
    this.startsAt = dateTimeToEpochMs(startsAt);
    this.nextBillingAt = dateTimeToEpochMs(nextBillingAt);
    this.status = status;
    this.paymentProcessor = paymentProcessor;
    this.paymentProcessorSubscriptionId = paymentProcessorSubscriptionId;
    this.paymentWalletId = paymentWalletId ?? undefined;
    this.productId = productId;
    this.productSku = toLegacyProductSku(productSku);
  }

  /** The gateway has no separate `subscriptionId`; `id` is its equivalent. */
  get subscriptionId() {
    return this.id;
  }

  get productItemId() {
    return this.productId.productItemId;
  }

  get productType() {
    return toProductType(this.productId.productType);
  }

  get isCancelled() {
    return this.status === Status.Cancelled;
  }

  get isFreeTrial() {
    return this.status === Status.FreeTrial;
  }

  get isInactive() {
    return this.status === Status.Inactive;
  }

  get isPastDue() {
    return this.status === Status.PastDue;
  }

  get hasSubscriptionNextBillDetails() {
    return STATUSES_WITH_NEXT_BILL_DETAILS.includes(this.status) && typeof this.nextBillingAt === 'number';
  }

  /**
   * s12n subscriptions carry SPECIALIZATION, matching naptime and `cancellationUndoUtils`. The enum
   * also defines SPECIALIZATION_SUBSCRIPTION, which production data does not appear to use.
   */
  get isSpecializationSubscription() {
    return this.productId.productType === ProductType.Specialization;
  }

  get isCatalogSubscription() {
    return this.productId.productType === ProductType.CatalogSubscription;
  }

  get isCredentialTrackSubscription() {
    return (
      this.productId.productType === ProductType.CredentialTrackSubscription ||
      this.productId.productType === ProductType.CredentialTrackSubscriptionV2
    );
  }

  get isCourseraPlusSubscription() {
    return this.productId.productType === ProductType.CourseraPlusSubscription;
  }

  get isCourseraLiteSubscription() {
    return this.productId.productType === ProductType.CourseraTierLite;
  }

  get isAppleInApp() {
    return APPLE_IN_APP_PROCESSORS.includes(this.paymentProcessor);
  }

  get paymentProcessorId(): string {
    return toPaymentProcessorId(this.paymentProcessor);
  }

  get isStripePayment() {
    return STRIPE_PROCESSORS.includes(this.paymentProcessor);
  }

  get isRazorpayPayment() {
    return RAZORPAY_PROCESSORS.includes(this.paymentProcessor);
  }

  get isWalletPayment() {
    return !!this.paymentWalletId && WALLET_PROCESSORS.includes(this.paymentProcessor);
  }

  get isStripeBv() {
    return STRIPE_BV_PROCESSORS.includes(this.paymentProcessor);
  }

  get isSandboxTransaction() {
    return SANDBOX_PROCESSORS.includes(this.paymentProcessor);
  }
}

export default Subscriptions;
